import { ReactNode, useMemo } from "react";
import Head from "next/head";

import { useRegisterPageSeo } from "../../seo-header/PageSeo";
import { getDefaultOgImage, PageSeoConfig } from "../../../methods/pageSeo";
import { PLATFORM_STATS } from "@vahak/core/dist/constants/platformStats";

const getCityMetaDescription = (cityName: string) =>
    `Book ${PLATFORM_STATS.liveLoadsAndLorries.label} loads and lorries in ${cityName}! Vahak is the #1 platform for ${cityName} transport services. Join India's largest transporter network today for free.`;

export type PageMetaTagsProps = PageSeoConfig & {
    children?: ReactNode;
};

/**
 * Registers page SEO with the global SeoProvider.
 * Rendering is handled by GlobalSeoHead in _app — route defaults live in methods/pageSeo.ts.
 */
const PageMetaTags = ({
    cityName,
    title: titleProp,
    description: descriptionProp,
    keywords = "",
    asPath,
    locale,
    localeNeutralPath,
    hreflangLocales,
    canonicalUrl: canonicalUrlProp,
    ogUrl,
    ogDescription,
    ogImgSrc = getDefaultOgImage(),
    ogImgAltText: ogImgAltTextProp,
    ogType,
    ogSiteName,
    schema,
    schemaOrgJsonUrl: schemaOrgJsonUrlProp,
    includeDefaultSchema = false,
    shouldBeIndexed = true,
    breadcrumbList,
    schemaType = "WebPage",
    faqList,
    children
}: PageMetaTagsProps) => {
    const title = titleProp ?? (cityName ? `Vahak - Book Best Transport Services in ${cityName}` : undefined);
    const description = descriptionProp ?? (cityName ? getCityMetaDescription(cityName) : undefined);
    const canonicalUrl = canonicalUrlProp ?? (cityName ? `https://www.vahak.in/city/${cityName}` : undefined);
    const schemaOrgJsonUrl = schemaOrgJsonUrlProp ?? (cityName ? `/city/${cityName}` : undefined);
    const ogImgAltText = ogImgAltTextProp ?? description;

    const seoConfig = useMemo<PageSeoConfig>(
        () => ({
            title,
            description,
            keywords,
            asPath,
            locale,
            localeNeutralPath,
            hreflangLocales,
            canonicalUrl: asPath ? undefined : canonicalUrl,
            ogUrl,
            ogDescription,
            ogImgSrc,
            ogImgAltText,
            ogType,
            ogSiteName,
            schema,
            schemaOrgJsonUrl,
            includeDefaultSchema,
            shouldBeIndexed,
            breadcrumbList,
            schemaType,
            faqList
        }),
        [
            title,
            description,
            keywords,
            asPath,
            locale,
            localeNeutralPath,
            hreflangLocales,
            canonicalUrl,
            ogUrl,
            ogDescription,
            ogImgSrc,
            ogImgAltText,
            ogType,
            ogSiteName,
            schema,
            schemaOrgJsonUrl,
            includeDefaultSchema,
            shouldBeIndexed,
            breadcrumbList,
            schemaType,
            faqList
        ]
    );

    useRegisterPageSeo(seoConfig);

    if (!children) {
        return null;
    }

    return <Head>{children}</Head>;
};

export default PageMetaTags;
